import { ItemTypes, MASKS_TYPE } from '../constants/constants';

// Décor de page (« page background » dans le code : le masque `background`
// date d'avant le renommage) : traits, formes et images posés sur chaque page
// du PDF, sur toute la page, sous le contenu, l'en-tête et le pied de page. Les
// pages pleine page (couverture, séparateurs, PDF importés en pleine page) n'en
// ont pas. Il ne se voit pas dans le document édité, seulement dans l'aperçu PDF
// et le PDF généré. Il se règle depuis son onglet des réglages, pas avec les
// masques, et s'édite seul, l'en-tête et le pied montrés par-dessus comme
// repères. Le service PDF l'imprime tel que l'éditeur le rend (pdfApp), en une
// page à part posée sous chaque page du document.
//
// Une SECTION peut avoir le sien, qui remplace celui du document sur ses pages
// (cf. pageBackgroundMode). Un document porte donc plusieurs décors : celui du
// modèle sous l'id historique `background`, et un par section à décor propre,
// baké à l'injection sous `background-section-<sectionId>`. Chaque container
// injecté porte l'id du décor qui lui revient (`pageBackgroundId`), résolu une
// fois pour toutes à l'injection en remontant la chaîne des sections parentes.

export const PAGE_BACKGROUND_ID = MASKS_TYPE.BACKGROUND.id;

// Les ids de décor atterrissent dans le DOM (#background-mask-pdf-<id>) : le
// préfixe reste un sélecteur CSS valide, et le sectionId le rend déterministe —
// réinjecter la même section (resynchro d'une section liée) réécrit son décor au
// lieu d'en empiler un second.
const SECTION_PAGE_BACKGROUND_PREFIX = 'background-section-';

export const sectionPageBackgroundId = (sectionId) =>
  `${SECTION_PAGE_BACKGROUND_PREFIX}${sectionId}`;

export const isPageBackgroundId = (id) =>
  id === PAGE_BACKGROUND_ID ||
  (typeof id === 'string' && id.startsWith(SECTION_PAGE_BACKGROUND_PREFIX));

export const isPageBackgroundMask = (mask) => isPageBackgroundId(mask?.id);

// Réglage d'une section : sans décor propre, ses pages gardent celui du
// document où elle est insérée. Avec, il le remplace — même vide, ce qui est la
// seule façon de laisser des pages sans décor dans un document qui en a un.
// Absent = `inherit` : les sections d'avant ne changent pas d'aspect.
export const PAGE_BACKGROUND_MODES = {
  INHERIT: 'inherit',
  OWN: 'own',
};

export const pageBackgroundMode = (configuration) =>
  configuration?.pageBackgroundMode === PAGE_BACKGROUND_MODES.OWN
    ? PAGE_BACKGROUND_MODES.OWN
    : PAGE_BACKGROUND_MODES.INHERIT;

export const hasOwnPageBackground = (configuration) =>
  pageBackgroundMode(configuration) === PAGE_BACKGROUND_MODES.OWN;

// Sous le contenu : un z-index négatif le peint avant tout le reste de son
// contexte d'empilement, box positionnées ou non. Le parent doit en former un
// (sinon le calque passerait aussi sous son fond blanc).
export const PAGE_BACKGROUND_Z_INDEX = -1;

// Un décor de page : traits, formes et images, et leurs groupements.
export const PAGE_BACKGROUND_BOX_TYPES = [
  ItemTypes.LINE,
  ItemTypes.SHAPE,
  ItemTypes.IMG,
  ItemTypes.GROUP_BOXES,
];

export const acceptsInPageBackground = (type) =>
  PAGE_BACKGROUND_BOX_TYPES.includes(type);

// Type de la box glissée (react-dnd). Une vignette du panneau (« newBox ») le
// porte dans l'objet glissé ; une box déjà posée qu'on déplace, non : c'est le
// type du glisser.
export const draggedBoxType = (itemType, item) =>
  itemType === 'newBox' ? item?.type : itemType;

export const findPageBackground = (masks) =>
  masks?.find(({ id }) => id === PAGE_BACKGROUND_ID);

export const findPageBackgroundById = (masks, id) =>
  masks?.find((m) => m.id === id);

// Tous les décors à dessiner du document : celui du modèle et ceux des sections
// à décor propre. Un décor vide n'en est pas : il n'a rien à imprimer, et c'est
// bien son rôle (il efface celui du document sur ses pages).
export const pageBackgrounds = (masks) =>
  (masks ?? []).filter(
    (m) => isPageBackgroundMask(m) && (m.boxes?.length ?? 0) > 0
  );

// Décor d'un container : celui baké à l'injection de sa section, sinon celui du
// document. Un container tapé directement dans le document n'a pas d'id baké.
export const containerPageBackgroundId = (container) =>
  container?.pageBackgroundId ?? PAGE_BACKGROUND_ID;

// Marges de la page tracées sur le décor : à son édition seulement (ni aperçu
// ni PDF), sauf si on les a cachées.
export const showsPageBackgroundMargins = ({ hidden, fromPdf, fromViewer }) =>
  !hidden && !fromPdf && !fromViewer;
